import { memo, useEffect, useRef, useState } from 'react';

/**
 * Split-flap (Solari di Udine) display, CSS-only 3D flip.
 *
 * Each character is a flap split in two halves (top/bottom). A character
 * change is rendered as a sequence of rotateX flips that cycle through the
 * charset from the current glyph to the target glyph, like a real mechanical
 * board. Only `transform`/`opacity` are animated → cheap, 60fps-friendly.
 *
 * SSR-safe: the first render is deterministic (it just paints the target
 * character statically); flipping starts only after mount, inside effects.
 */

// Charset approved in TASK-067. Order defines the flip cycle.
export const FLAP_CHARSET = " ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.'\"ÀÈÌÒÙ-";

const CHARSET_INDEX = new Map<string, number>();
for (let i = 0; i < FLAP_CHARSET.length; i += 1) {
  CHARSET_INDEX.set(FLAP_CHARSET[i], i);
}

// A glyph not in the charset degrades gracefully: shown statically, no flip.
function normalize(char: string): string {
  const upper = char.toUpperCase();
  return CHARSET_INDEX.has(upper) ? upper : char;
}

function steps(from: string, to: string): string[] {
  const fromIdx = CHARSET_INDEX.get(from);
  const toIdx = CHARSET_INDEX.get(to);
  // Unknown source or target → jump straight to the target, no intermediate flaps.
  if (fromIdx === undefined || toIdx === undefined) return [to];
  const out: string[] = [];
  let idx = fromIdx;
  while (idx !== toIdx) {
    idx = (idx + 1) % FLAP_CHARSET.length;
    out.push(FLAP_CHARSET[idx]);
  }
  return out.length ? out : [to];
}

type SplitFlapCharProps = {
  target: string;
  /** Flag the page in reduced-motion mode → render statically, no flips. */
  reducedMotion: boolean;
  /** Per-flip duration in ms. */
  flipMs?: number;
};

type FlapState = {
  // The settled glyph currently shown by the static halves.
  display: string;
  // The outgoing glyph painted on the falling flap (null = not flipping).
  folding: string | null;
  // Bumps on every flip so the falling-flap animation restarts.
  tick: number;
};

const SplitFlapChar = memo(({ target, reducedMotion, flipMs = 55 }: SplitFlapCharProps) => {
  const wanted = normalize(target);
  // Deterministic first paint: show the final glyph (SSR === client).
  const [state, setState] = useState<FlapState>({ display: wanted, folding: null, tick: 0 });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (reducedMotion) {
      setState((s) => (s.display === wanted ? s : { display: wanted, folding: null, tick: s.tick }));
      return;
    }

    const queue = steps(state.display, wanted);
    if (queue.length === 1 && queue[0] === state.display) return;

    const advance = () => {
      const next = queue.shift();
      if (next === undefined) {
        setState((s) => ({ ...s, folding: null }));
        return;
      }
      setState((s) => ({ display: next, folding: s.display, tick: s.tick + 1 }));
      timerRef.current = setTimeout(advance, flipMs);
    };

    advance();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Keyed on the requested target only; `state.display` mutates internally
    // and including it would restart the cascade mid-flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, reducedMotion, flipMs]);

  const { display, folding, tick } = state;

  return (
    <span className="flap" aria-hidden="true">
      {/* Top half: .flap-inner sits at top:0, showing the upper slice of the glyph. */}
      <span className="flap-half flap-top">
        <span className="flap-inner">
          <span className="flap-glyph">{display}</span>
        </span>
      </span>
      {/* Bottom half: .flap-inner sits at bottom:0, showing the lower slice. */}
      <span className="flap-half flap-bottom">
        <span className="flap-inner">
          <span className="flap-glyph">{display}</span>
        </span>
      </span>
      {/* Falling flap: top-half panel for the outgoing glyph rotates down. */}
      {folding !== null && !reducedMotion && (
        <span key={tick} className="flap-fold" style={{ animationDuration: `${flipMs}ms` }}>
          <span className="flap-half flap-top flap-fold-top">
            <span className="flap-inner">
              <span className="flap-glyph">{folding}</span>
            </span>
          </span>
        </span>
      )}
    </span>
  );
});
SplitFlapChar.displayName = 'SplitFlapChar';

type SplitFlapTextProps = {
  /** Logical text; rendered right-padded/truncated to `length` cells. */
  text: string;
  length: number;
  reducedMotion: boolean;
  align?: 'left' | 'right';
  flipMs?: number;
  className?: string;
};

export const SplitFlapText = memo(
  ({ text, length, reducedMotion, align = 'left', flipMs, className }: SplitFlapTextProps) => {
    const upper = text.toUpperCase().slice(0, length);
    const cells = (align === 'right' ? upper.padStart(length, ' ') : upper.padEnd(length, ' ')).split(
      '',
    );

    return (
      <span className={`flap-row${className ? ` ${className}` : ''}`}>
        {cells.map((char, i) => (
          <SplitFlapChar
            // Cells are positional and never reordered → stable index key.
            key={i}
            target={char}
            reducedMotion={reducedMotion}
            flipMs={flipMs}
          />
        ))}
      </span>
    );
  },
);
SplitFlapText.displayName = 'SplitFlapText';
